import { Injectable } from '@angular/core';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import * as _ from 'lodash';
import { CompanyRoles, LicencedModuleXatenaModules } from 'net-api-lib';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { AuthService } from './auth/auth.service';

export type UserRolesEnum =
  | 'admin'
  | 'profileadmin'
  | 'rfx.accessallprojects'
  | 'rfx.createproject'
  | 'request.accessallrequests'
  | 'request.admin'
  | 'cartAnalytics.accessmodule'
  | 'recall.accessmodule'
  | 'contract.accessmodule'
  | 'catalog.accessmodule'
  | 'masterData.accessmodule'
  | 'supplyIncident.accessmodule'
  | 'srm.accessmodule'
  | 'request.accessinvolvedrequests'
  | 'secondMarket.accessmodule'
  | 'miniTender.admin';

export const UserRolesEnum = {
  Admin: 'admin' as UserRolesEnum,
  Profileadmin: 'profileadmin' as UserRolesEnum,
  RfxAccessallprojects: 'rfx.accessallprojects' as UserRolesEnum,
  RfxCreateproject: 'rfx.createproject' as UserRolesEnum,
  RequestAccessallrequests: 'request.accessallrequests' as UserRolesEnum,
  RequestAdmin: 'request.admin' as UserRolesEnum,
  CartAnalyticsAccessmodule: 'cartAnalytics.accessmodule' as UserRolesEnum,
  RecallAccessmodule: 'recall.accessmodule' as UserRolesEnum,
  ContractAccessmodule: 'contract.accessmodule' as UserRolesEnum,
  CatalogAccessmodule: 'catalog.accessmodule' as UserRolesEnum,
  MasterDataAccessmodule: 'masterData.accessmodule' as UserRolesEnum,
  SupplyIncidentAccessmodule: 'supplyIncident.accessmodule' as UserRolesEnum,
  SrmAccessmodule: 'srm.accessmodule' as UserRolesEnum,
  RequestAccessinvolvedrequests:
    'request.accessinvolvedrequests' as UserRolesEnum,
  SecondMarketAccessmodule: 'secondMarket.accessmodule' as UserRolesEnum,
  MiniTenderAdmin: 'miniTender.admin' as UserRolesEnum,
};

export interface UserInfo {
  id: string;
  firstName: string;
  lastName: string;
  permissions: UserRolesEnum[];
  requestGroups?: string[];

  company: {
    id: number;
    role: CompanyRoles;
    licencedModules: LicencedModuleXatenaModules[];
    expiredModules: LicencedModuleXatenaModules[];
  };

  impersonationUserOid?: number;
}

export interface XatenaJwtPayload extends JwtPayload {
  given_name: string;
  family_name: string;
  role: string[];
  companyId: number;
  companyRole: string[];
  companyLicencedModule: LicencedModuleXatenaModules[];
  companyExpiredModule: LicencedModuleXatenaModules[];
  impersonationUserOid?: number;
}

@Injectable({
  providedIn: 'root',
})
export class UserInfoService {
  private userInfo = new BehaviorSubject<UserInfo | undefined>(undefined);

  constructor(private authService: AuthService) {}

  /**
   * returns current userInfo.value from BehaviorSubject, this only works because of an APP_INITIALIZER
   * and is only used temporary in RoleRoutingModule
   *
   * remove asap
   * @returns
   */
  _getUserInfo(): UserInfo | undefined {
    return this.userInfo.value;
  }

  getUserInfo(): Observable<UserInfo | undefined> {
    return this.authService.getAccessToken().pipe(
      map(token => {
        if (!token) return undefined;

        const payload = jwtDecode<XatenaJwtPayload>(token);

        return {
          id: payload.sub ?? '',
          firstName: payload.given_name ?? '',
          lastName: payload.family_name ?? '',
          permissions: payload.role ?? [],
          company: {
            id: payload.companyId ?? 0,
            role: payload.companyRole[0] as CompanyRoles,
            licencedModules: payload.companyLicencedModule,
            expiredModules: payload.companyExpiredModule ?? [],
          },
          impersonationUserOid: payload.impersonationUserOid,
        } as UserInfo;
      }),
      distinctUntilChanged(_.isEqual),
      tap(m => this.userInfo.next(m))
    );
  }

  isAuthenticated(): Observable<boolean> {
    return this.authService.isAuthenticated();
  }

  isAdmin(): Observable<boolean> {
    return this.getUserInfo().pipe(
      map(m => {
        return m?.permissions.includes(UserRolesEnum.Admin) ?? false;
      })
    );
  }

  isImpersonating(): Observable<boolean> {
    return this.getUserInfo().pipe(map(m => !!m?.impersonationUserOid));
  }

  isCompanyAdmin(): Observable<boolean> {
    return this.getUserInfo().pipe(
      map(m => {
        return m?.permissions.includes(UserRolesEnum.Profileadmin) ?? false;
      })
    );
  }

  isUszUser(): Observable<boolean> {
    //const uszIds = [65447577, 65978168, 67976021]; // uat, prod & solzer ag
    const uszIds: number[] = [65978168]; // only prod
    return this.getUserInfo().pipe(
      map(m => uszIds.includes(m?.company.id ?? 0))
    );
  }

  isSupplier(): Observable<boolean> {
    return this.getUserInfo().pipe(
      map(m => m?.company.role == CompanyRoles.Vendor)
    );
  }

  isBuyer(): Observable<boolean> {
    return this.getUserInfo().pipe(
      map(m => m?.company.role == CompanyRoles.Purchaser)
    );
  }

  getUserPermissions(): Observable<UserRolesEnum[] | undefined> {
    return this.getUserInfo().pipe(map(m => m?.permissions));
  }

  hasPermission(permission: UserRolesEnum): Observable<boolean> {
    return this.getUserPermissions().pipe(
      map(m => m?.includes(permission) ?? false)
    );
  }
}
